import { apiGet, apiPost, apiPatch, apiDelete } from "../api-client"

/**
 * A member of a workspace
 */
export interface WorkspaceMember {
  id: string
  workspace: string
  user: string
  user_email: string
  user_name: string
  created_at: string
}

/**
 * Workspace represents a logical grouping of wells within a tenant
 * Used for multi-client operations and bulk well management
 */
export interface Workspace {
  id: string
  tenant: string
  tenant_slug: string
  name: string
  operator_number?: string
  description?: string
  is_active: boolean
  /** @deprecated Use filing_count instead — well_count is retained for API backwards compatibility */
  well_count: number
  filing_count: number
  created_at: string
  updated_at: string
}

/**
 * Paginated response for workspace list
 */
export interface WorkspaceListResponse {
  count: number
  next: string | null
  previous: string | null
  results: Workspace[]
}

/**
 * Request body for creating a new workspace
 */
export interface CreateWorkspaceRequest {
  name: string
  operator_number?: string
  description?: string
  is_active?: boolean
}

/**
 * Request body for updating an existing workspace
 */
export interface UpdateWorkspaceRequest {
  name?: string
  operator_number?: string
  description?: string
  is_active?: boolean
}

/**
 * Query parameters for listing workspaces
 */
export interface ListWorkspacesParams {
  is_active?: boolean
  page?: number
  page_size?: number
}

/**
 * Fetch all workspaces for the current tenant
 * @param params - Optional query parameters for filtering and pagination
 * @returns Paginated list of workspaces
 */
export async function fetchWorkspaces(
  params?: ListWorkspacesParams
): Promise<WorkspaceListResponse> {
  const searchParams = new URLSearchParams()

  if (params?.is_active !== undefined) {
    searchParams.set("is_active", params.is_active.toString())
  }

  if (params?.page) {
    searchParams.set("page", params.page.toString())
  }

  if (params?.page_size) {
    searchParams.set("page_size", params.page_size.toString())
  }

  const queryString = searchParams.toString()
  const url = `/api/tenant/workspaces/${queryString ? "?" + queryString : ""}`

  const response = await apiGet<WorkspaceListResponse>(url)
  return response
}

/**
 * Create a new workspace
 * @param data - Workspace creation data
 * @returns The created workspace
 */
export async function createWorkspace(
  data: CreateWorkspaceRequest
): Promise<Workspace> {
  const response = await apiPost<Workspace>("/api/tenant/workspaces/", data)
  return response
}

/**
 * Fetch a single workspace by ID
 * @param id - Workspace ID (UUID)
 * @returns The workspace details
 */
export async function fetchWorkspace(id: string): Promise<Workspace> {
  const response = await apiGet<Workspace>(`/api/tenant/workspaces/${id}/`)
  return response
}

/**
 * Update an existing workspace
 * @param id - Workspace ID (UUID)
 * @param data - Fields to update
 * @returns The updated workspace
 */
export async function updateWorkspace(
  id: string,
  data: UpdateWorkspaceRequest
): Promise<Workspace> {
  const response = await apiPatch<Workspace>(
    `/api/tenant/workspaces/${id}/`,
    data
  )
  return response
}

/**
 * Delete a workspace
 * @param id - Workspace ID (UUID)
 */
export async function deleteWorkspace(id: string): Promise<void> {
  await apiDelete<void>(`/api/tenant/workspaces/${id}/`)
}

/**
 * Archive a workspace (soft delete)
 * Sets is_active=false without deleting the record
 * @param id - Workspace ID (UUID)
 * @returns The archived workspace
 */
export async function archiveWorkspace(id: string): Promise<Workspace> {
  const response = await apiPost<Workspace>(
    `/api/tenant/workspaces/${id}/archive/`,
    {}
  )
  return response
}

/**
 * Restore an archived workspace
 * Sets is_active=true to restore access
 * @param id - Workspace ID (UUID)
 * @returns The restored workspace
 */
export async function restoreWorkspace(id: string): Promise<Workspace> {
  const response = await apiPost<Workspace>(
    `/api/tenant/workspaces/${id}/restore/`,
    {}
  )
  return response
}

/**
 * Fetch all members of a workspace (admin only)
 * @param workspaceId - Workspace ID (UUID)
 * @returns List of workspace members
 */
export async function getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const response = await apiGet<WorkspaceMember[]>(
    `/api/tenant/workspaces/${workspaceId}/members/`
  )
  return response
}

/**
 * Add a member to a workspace (admin only)
 * @param workspaceId - Workspace ID (UUID)
 * @param userId - User ID to add
 * @returns The created workspace member record
 */
export async function addWorkspaceMember(workspaceId: string, userId: string): Promise<WorkspaceMember> {
  const response = await apiPost<WorkspaceMember>(
    `/api/tenant/workspaces/${workspaceId}/members/`,
    { user_id: userId }
  )
  return response
}

/**
 * Remove a member from a workspace (admin only)
 * @param workspaceId - Workspace ID (UUID)
 * @param userPk - Primary key of the user to remove
 */
export async function removeWorkspaceMember(workspaceId: string, userPk: string): Promise<void> {
  await apiDelete<void>(`/api/tenant/workspaces/${workspaceId}/members/${userPk}/`)
}
